// This file is for rendering the filter menu options in the filter section
// It is used to show include/exclude options as well as filter type options.

import { Menu, MenuItem, Divider, Tooltip, Radio, Checkbox } from '@mui/material';
import { fontSize } from '../..';
import { Dispatch, SetStateAction } from 'react';

interface FilterMenuOptionsProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  includeExcludeOptions: string[];
  filterOptions: string[];
  selectedOption: string;
  includeExcludeValue: string;
  filterType: string;
  showPickListOptions?: boolean;
  selectedSortOptions?: string[];
  isChartFilter?: string;
  onOptionClick: (section: string, value: string) => void;
  from?: string;
  parameterized?: boolean;
  setShowSourceDetails?: (value: boolean) => void;
  parameterizeChatProperty?: () => void;
  disableAggregated?: boolean;
  // If From chart FIlter and isOverrideFilter is true, then  dont show Aggregated filter types
  isOverrideFilter?: boolean;
}

const FilterMenuOptions = ({
  anchorEl,
  open,
  onClose,
  includeExcludeOptions,
  filterOptions,
  selectedOption,
  includeExcludeValue,
  filterType,
  showPickListOptions = false,
  selectedSortOptions = [],
  isChartFilter,
  onOptionClick,
  from,
  parameterized,
  setShowSourceDetails,
  parameterizeChatProperty,
  isOverrideFilter = false,
  disableAggregated = false,
}: FilterMenuOptionsProps) => {
  const sortOptions = ['Sort Desc', 'Remove Blank', 'Blank at Bottom', 'Single Selection'];
  const onClick = (opt: string) => {
    if (disableAggregated && ['Aggregate Condition', 'Top N'].includes(opt)) return;
    onOptionClick('opt2', opt);
  };
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiPaper-root': {
          minWidth: '160px',
        },
      }}
    >
      {filterOptions.map((opt2, i) => {
        if (
          from === 'chartFilter' &&
          isOverrideFilter &&
          ['Aggregate Condition', 'Top N'].includes(opt2)
        ) {
          return null;
        }
        return (
          <div key={i}>
            <div style={{ display: 'flex' }} onClick={() => onClick(opt2)}>
              <Tooltip
                title={opt2 === filterType ? 'Selected' : null}
                slotProps={{
                  tooltip: {
                    sx: {
                      fontSize: '12px',
                    },
                  },
                }}
              >
                <Radio
                  checked={opt2 === selectedOption}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      fontSize: '12px',
                      height: '12px',
                      color: '#af99db',
                    },
                    alignSelf: 'center',
                    marginLeft: '5px',
                  }}
                  disabled={disableAggregated && ['Aggregate Condition', 'Top N'].includes(opt2)}
                />
              </Tooltip>
              <MenuItem
                disabled={disableAggregated && ['Aggregate Condition', 'Top N'].includes(opt2)}
                sx={{
                  flex: 1,
                  fontSize: '12px',
                  padding: '2px 0px',
                  paddingRight: '1rem',
                }}
              >
                {opt2}
              </MenuItem>
            </div>

            {showPickListOptions && opt2 === 'Pick List' && selectedOption === 'Pick List' && (
              <div style={{ marginLeft: '20px' }}>
                {sortOptions.map((sortOpt, j) => {
                  if (isChartFilter === 'chartFilter' && sortOpt === 'Single Selection')
                    return null;
                  return (
                    <div
                      key={j}
                      style={{ display: 'flex' }}
                      onClick={() => {
                        onOptionClick('sort', sortOpt);
                      }}
                    >
                      <Tooltip
                        title={selectedSortOptions.includes(sortOpt) ? 'Selected' : null}
                        slotProps={{
                          tooltip: {
                            sx: {
                              fontSize: '12px',
                            },
                          },
                        }}
                      >
                        <Checkbox
                          checked={selectedSortOptions.includes(sortOpt)}
                          sx={{
                            '& .MuiSvgIcon-root': {
                              fontSize: '12px',
                              height: '12px',
                              color: '#af99db',
                            },
                            alignSelf: 'center',
                            marginLeft: '5px',
                          }}
                        />
                      </Tooltip>
                      <MenuItem
                        sx={{
                          flex: 1,
                          fontSize: '12px',
                          padding: '2px 0px',
                          paddingRight: '1rem',
                        }}
                      >
                        {sortOpt === 'Remove Blank'
                          ? 'Remove (Blank) values'
                          : sortOpt === 'Blank at Bottom'
                            ? '(Blank) at bottom'
                            : sortOpt}
                      </MenuItem>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      <Divider sx={{ margin: '5px 0px' }} />

      {includeExcludeOptions.map((opt, i) => (
        <div key={i} style={{ display: 'flex' }} onClick={() => onOptionClick('opt1', opt)}>
          <Tooltip
            title={opt === includeExcludeValue ? 'Selected' : null}
            slotProps={{
              tooltip: {
                sx: {
                  fontSize: '12px',
                },
              },
            }}
          >
            <Radio
              checked={opt === includeExcludeValue}
              disabled={opt === 'Exclude' && filterType === 'Relative Filter'}
              sx={{
                '& .MuiSvgIcon-root': {
                  fontSize: '12px',
                  height: '12px',
                  color: opt === 'Exclude' ? '#ffb74d' : '#af99db',
                },
                alignSelf: 'center',
                marginLeft: '5px',
              }}
            />
          </Tooltip>
          <MenuItem
            disabled={opt === 'Exclude' && filterType === 'Relative Filter'}
            sx={{
              fontSize: '12px',
              alignSelf: 'center',
              padding: '2px 0px',
              flex: 1,
            }}
          >
            {opt}
          </MenuItem>
        </div>
      ))}

      {from === 'chartFilter' && (
        <>
          <Divider sx={{ margin: '2px 0px' }} />
          <div style={{ display: 'flex' }} onClick={parameterizeChatProperty}>
            <Tooltip
              title={parameterized ? 'Parameterized' : 'Not Parameterized'}
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: '12px',
                  },
                },
              }}
            >
              <>
                <Checkbox
                  checked={parameterized!}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      fontSize: fontSize.medium,
                      height: '12px',
                      color: '#af99db',
                    },
                    alignSelf: 'center',
                    marginLeft: '5px',
                  }}
                  disabled={selectedOption === 'Relative Filter'}
                />
                <MenuItem
                  sx={{
                    fontSize: '12px',
                    alignSelf: 'center',
                    padding: '2px 0px',
                    flex: 1,
                  }}
                  disabled={selectedOption === 'Relative Filter'}
                >
                  Parameterized
                </MenuItem>
              </>
            </Tooltip>
          </div>
        </>
      )}
      <Divider sx={{ margin: '2px 0px' }} />
      <div
        style={{ display: 'flex' }}
        onClick={() => {
          if (setShowSourceDetails) setShowSourceDetails(true);
        }}
      >
        <div
          style={{
            // width: '32px',
            marginLeft: '32px',
          }}
        />
        <MenuItem
          sx={{
            fontSize: '12px',
            alignSelf: 'center',
            padding: '2px 0px',
            flex: 1,
          }}
        >
          Source Details
        </MenuItem>
      </div>
    </Menu>
  );
};

export default FilterMenuOptions;
